<template>
  <div class="ai-validation-result" :class="containerClass">
    <div v-if="scoreTitle" :class="scoreTitleClass">
      {{ scoreTitle }}
    </div>
    <q-circular-progress
      v-if="normalizedScore !== undefined"
      show-value
      class="q-my-md"
      :value="normalizedScore"
      size="70px"
      :color="getScoreColor(normalizedScore)"
      :min="1"
      :max="100"
    />

    <div v-if="issues?.length" :class="issuesTitleClass">
      {{ issuesTitle }}
    </div>
    <ul v-if="issues?.length">
      <li v-for="issue in issues" :key="issue.title">
        <q-icon name="circle" class="q-mr-xs" :title="issue.severity" :color="getIssueColor(issue.severity)" />
        <b>{{ issue.title }}</b> - {{ issue.message }}
      </li>
    </ul>

    <div v-if="suggestions?.length" :class="suggestionsTitleClass" class="q-mt-lg">
      {{ suggestionsTitle }}
    </div>
    <ul v-if="suggestions?.length">
      <li v-for="suggestion in suggestions" :key="suggestion">
        {{ suggestion }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { hubClient } from '@tfpl/api-client';
import { computed } from 'vue';

interface ValidationIssue {
  title: string;
  message: string;
  severity: hubClient.ValidateVisionIssueDto['severity'];
}

type Props = {
  score?: number | null;
  issues?: ValidationIssue[] | null;
  suggestions?: string[] | null;
  scoreTitle?: string;
  issuesTitle?: string;
  suggestionsTitle?: string;
  scoreTitleClass?: string;
  issuesTitleClass?: string;
  suggestionsTitleClass?: string;
  containerClass?: string | string[] | Record<string, boolean>;
};

const props = withDefaults(defineProps<Props>(), {
  issues: () => [],
  suggestions: () => [],
  score: null,
  scoreTitle: '',
  issuesTitle: '',
  suggestionsTitle: '',
  scoreTitleClass: 'text-h6',
  issuesTitleClass: 'text-h6',
  suggestionsTitleClass: 'text-h6',
  containerClass: '',
});

const normalizedScore = computed(() => {
  if (props.score === undefined || props.score === null) {
    return undefined;
  }
  if (!Number.isFinite(props.score)) {
    return 0;
  }
  return Math.max(1, Math.min(100, Math.round(props.score)));
});

const getIssueColor = (issue: hubClient.ValidateVisionIssueDto['severity']): string => {
  switch (issue) {
    case 'low':
      return 'blue';
    case 'medium':
      return 'yellow';
    case 'high':
      return 'red';
    default:
      return 'grey';
  }
};

const getScoreColor = (score: number): string => {
  if (score < 40) return 'red';
  if (score < 70) return 'yellow';
  return 'green';
};
</script>

<style scoped lang="scss">
.ai-validation-result ul {
  padding-left: 1.25rem;
}
</style>
